// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankUploadTemplateEntity } from '@client/banks/utils/download-bank-upload-template';
import type { TFunction } from 'i18next';

/** Domain field path → create-bank label key (aggregator ns). */
const FIELD_LABEL_KEYS: Record<BankUploadTemplateEntity, Record<string, string>> = {
  accounts: {
    iban: 'aggregator.create-bank.accounts.iban',
    name: 'aggregator.create-bank.accounts.name',
    currency: 'aggregator.create-bank.accounts.currency',
    accountType: 'aggregator.create-bank.accounts.type-label',
    available: 'aggregator.create-bank.accounts.available',
    balanceDate: 'aggregator.create-bank.accounts.balance-date',
    owners: 'aggregator.create-bank.accounts.owner-name',
  },
  positions: {
    iban: 'aggregator.create-bank.positions.account',
    isin: 'aggregator.create-bank.positions.isin',
    name: 'aggregator.create-bank.positions.name',
    currency: 'aggregator.create-bank.accounts.currency',
    assetType: 'aggregator.create-bank.positions.asset-type',
    quantity: 'aggregator.create-bank.positions.quantity',
    price: 'aggregator.create-bank.positions.price',
    acquisitionCost: 'aggregator.create-bank.positions.acquisition-cost',
    snapshotDate: 'aggregator.create-bank.positions.valuation-date',
  },
  transactions: {
    iban: 'aggregator.create-bank.transactions.account',
    transactionDate: 'aggregator.create-bank.transactions.transaction-date-time',
    amount: 'aggregator.create-bank.transactions.amount',
    currency: 'aggregator.create-bank.accounts.currency',
    description: 'aggregator.create-bank.transactions.description',
    name: 'aggregator.create-bank.transactions.name',
    type: 'aggregator.create-bank.transactions.type',
    isin: 'aggregator.create-bank.transactions.isin',
    quantity: 'aggregator.create-bank.transactions.quantity',
    price: 'aggregator.create-bank.transactions.price',
    assetType: 'aggregator.create-bank.transactions.asset-type',
    subtype: 'aggregator.create-bank.transactions.subtype',
  },
  debts: {
    iban: 'aggregator.create-bank.debts.account',
    debtIban: 'aggregator.create-bank.debts.account',
    productType: 'aggregator.create-bank.debts.product-type',
    name: 'aggregator.create-bank.debts.name',
    currency: 'aggregator.create-bank.accounts.currency',
    pendingAmount: 'aggregator.create-bank.debts.pending-amount',
    snapshotDate: 'aggregator.create-bank.debts.snapshot-date',
    contractNumber: 'aggregator.create-bank.debts.contract-number',
    grantedCapital: 'aggregator.create-bank.debts.granted-capital',
    interestRate: 'aggregator.create-bank.debts.interest-rate',
    limitAmount: 'aggregator.create-bank.debts.credit-limit',
    constitutionDate: 'aggregator.create-bank.debts.constitution-date',
    expirationDate: 'aggregator.create-bank.debts.expiration-date',
    nextInstallment: 'aggregator.create-bank.debts.next-installment',
    pendingReceipts: 'aggregator.create-bank.debts.pending-receipts',
    available: 'aggregator.create-bank.debts.available',
    disposed: 'aggregator.create-bank.debts.disposed',
    currentBalance: 'aggregator.create-bank.debts.current-balance',
    nextPaymentDate: 'aggregator.create-bank.debts.next-payment-date',
    installmentDate: 'aggregator.create-bank.debts.installment-date',
    status: 'aggregator.create-bank.debts.amortization-status',
    amortized: 'aggregator.create-bank.debts.amortized',
    interest: 'aggregator.create-bank.debts.interest-amount',
    total: 'aggregator.create-bank.debts.total-installment',
    pendingCapital: 'aggregator.create-bank.debts.pending-capital',
  },
};

const snakeToCamel = (value: string): string =>
  value.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());

/** Normalize Zod paths like `owners.0.name` → `owners`. */
export const normalizeBankUploadFieldPath = (fieldPath: string): string => {
  const segments = fieldPath.split('.').filter(Boolean);

  if (segments.length === 0) {
    return fieldPath;
  }

  const head = snakeToCamel(segments[0]);

  if (head === 'owners') {
    return 'owners';
  }

  return head;
};

export const getBankUploadExcelFieldLabel = (
  entity: BankUploadTemplateEntity,
  fieldPath: string | undefined,
  t: TFunction,
): string => {
  if (!fieldPath) {
    return t('aggregator.create-bank.excel.import.field-fallback', {
      ns: 'aggregator',
    });
  }

  const normalized = normalizeBankUploadFieldPath(fieldPath);
  const key = FIELD_LABEL_KEYS[entity]?.[normalized];

  if (!key) {
    return normalized;
  }

  return t(key, {
    ns: 'aggregator',
    index: 1,
    defaultValue: normalized,
  });
};
